import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';

import { ROUTES } from 'theSource/app/routes';
import {
  selectIsAuthenticated,
  selectIsPasswordChanged,
  selectTermsOfUseAccepted,
  setRedirectLink,
  PostLoginDownloadPrompt,
  selectIsAdmin,
  selectMfaEnabled,
  selectMfaConfigured,
  selectMfaRequired,
} from 'theSource/features';
import { NativeAuthDialog } from 'theSource/components';

interface PrivateOutletProps {
  sourceEnabled: boolean;
  children?: JSX.Element;
}

export const PrivateOutlet = ({
  sourceEnabled,
  children,
}: PrivateOutletProps) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const termsOfUseAccepted = useSelector(selectTermsOfUseAccepted);
  const isPasswordChanged = useSelector(selectIsPasswordChanged);
  const isAdmin = useSelector(selectIsAdmin);
  const mfaConfigured = useSelector(selectMfaConfigured);
  const mfaRequired = useSelector(selectMfaRequired);
  const mfaEnabled = useSelector(selectMfaEnabled);

  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(setRedirectLink(location.pathname));
  }, [dispatch, location.pathname]);

  if (isPasswordChanged === false) {
    return <Navigate to={ROUTES.public.changePassword.path} />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.public.login.path} />; // Go back to login if not logged in
  }

  if (!termsOfUseAccepted && !isAdmin) {
    return <Navigate to={ROUTES.public.termsAndConditions.path} />;
  }

  if (mfaEnabled && mfaRequired && !mfaConfigured && !isAdmin) {
    return <Navigate to={ROUTES.additional.account.securityPreferences.path} />;
  }

  if (!sourceEnabled) {
    // we don't want to show The PWA prompts for non-source orgs
    return (
      <>
        {children}
        <Outlet />
      </>
    );
  }

  return (
    <>
      {children}
      <NativeAuthDialog />
      <PostLoginDownloadPrompt />
      <Outlet />
    </>
  );
};
